import { last, sumBy } from 'lodash';

export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/csv',
];

export const BLOCKED_ATTACHMENT_EXTENSIONS = [
  '.exe',
  '.bat',
  '.sh',
  '.scr',
  '.js',
  '.vbs',
  '.cmd',
  '.com',
  '.msi',
];

export const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
export const MAX_TOTAL_ATTACHMENTS_SIZE_BYTES = 22 * 1024 * 1024;

export interface IAttachmentValidationResult {
  valid: boolean;
  reason?: string;
}

export function validateEmailAttachment(
  file: File
): IAttachmentValidationResult {
  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    return {
      valid: false,
      reason: `File size exceeds the maximum allowed size of 10 MB. Your file is ${(
        file.size /
        (1024 * 1024)
      ).toFixed(2)} MB.`,
    };
  }

  const parts = file.name.split('.');
  const extension = last(parts);
  if (parts.length < 2 || !extension) {
    return {
      valid: false,
      reason: `File must have a file extension.`,
    };
  }

  const fileExtension = '.' + extension.toLowerCase();
  if (BLOCKED_ATTACHMENT_EXTENSIONS.includes(fileExtension)) {
    return {
      valid: false,
      reason: `File type ${fileExtension} is not allowed. Please use a different file format.`,
    };
  }

  if (!ALLOWED_ATTACHMENT_MIME_TYPES.includes(file.type)) {
    return {
      valid: false,
      reason: `File type is not supported. Allowed types: PDF, Word, Excel, images (JPEG, PNG, GIF, WebP), text, and CSV files.`,
    };
  }

  return { valid: true };
}

export function validateTotalAttachmentSize(
  files: { size: number }[]
): IAttachmentValidationResult {
  const totalSize = sumBy(files, 'size');

  if (totalSize > MAX_TOTAL_ATTACHMENTS_SIZE_BYTES) {
    return {
      valid: false,
      reason: `Total attachment size exceeds the maximum allowed size of 22 MB. Your total is ${(
        totalSize /
        (1024 * 1024)
      ).toFixed(2)} MB.`,
    };
  }

  return { valid: true };
}
